import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'rsuite'

interface CropResolution {
  width: number;
  height: number;
}

interface Position {
  x: number;
  y: number;
}

interface ImageCropModalProps {
  open: boolean;
  file: File | null;
  resolution: CropResolution;
  title?: string;
  onClose(): void;
  onApply(file: File): void;
}

const MAX_FRAME_WIDTH = 640
const MAX_FRAME_HEIGHT = 420

function getCropFrameSize(width: number, height: number) {
  const aspectRatio = width / height
  let frameWidth = Math.min(MAX_FRAME_WIDTH, MAX_FRAME_HEIGHT * aspectRatio)
  let frameHeight = frameWidth / aspectRatio

  if (frameHeight > MAX_FRAME_HEIGHT) {
    frameHeight = MAX_FRAME_HEIGHT
    frameWidth = frameHeight * aspectRatio
  }

  return { width: frameWidth, height: frameHeight }
}

function getFileName(name: string, type: string) {
  const ext = type.split('/')[1] || 'jpg'
  const baseName = name.replace(/\.[^/.]+$/, '')
  return `${baseName}-cropped.${ext}`
}

export default function ImageCropModal({
  open,
  file,
  resolution,
  title = 'Crop Image',
  onClose,
  onApply
}: ImageCropModalProps) {
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
  const [imageSrc, setImageSrc] = useState('')
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 })
  const [dragStart, setDragStart] = useState<Position | null>(null)
  const [frameSize, setFrameSize] = useState(() => getCropFrameSize(resolution.width, resolution.height))
  const [scale, setScale] = useState(1)
  const [minScale, setMinScale] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const displayedImageWidth = useMemo(() => (imageElement ? imageElement.width * scale : 0), [imageElement, scale])
  const displayedImageHeight = useMemo(() => (imageElement ? imageElement.height * scale : 0), [imageElement, scale])

  const clampPosition = useCallback((x: number, y: number, nextScale: number) => {
    if (!imageElement) {
      return { x: 0, y: 0 }
    }

    const imgWidth = imageElement.width * nextScale
    const imgHeight = imageElement.height * nextScale
    const minX = Math.min(0, frameSize.width - imgWidth)
    const minY = Math.min(0, frameSize.height - imgHeight)

    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y))
    }
  }, [frameSize.height, frameSize.width, imageElement])

  useEffect(() => {
    if (!open || !file) {
      return
    }

    setIsLoading(true)
    const localImageUrl = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      const cropFrame = getCropFrameSize(resolution.width, resolution.height)
      const initialScale = Math.max(cropFrame.width / image.width, cropFrame.height / image.height)
      const initialPosition = {
        x: (cropFrame.width - image.width * initialScale) / 2,
        y: (cropFrame.height - image.height * initialScale) / 2
      }

      setFrameSize(cropFrame)
      setImageElement(image)
      setImageSrc(localImageUrl)
      setMinScale(initialScale)
      setScale(initialScale)
      setPosition(initialPosition)
      setIsLoading(false)
    }
    image.src = localImageUrl

    return () => {
      URL.revokeObjectURL(localImageUrl)
    }
  }, [file, open, resolution.height, resolution.width])

  function handlePointerDown(clientX: number, clientY: number) {
    setDragStart({ x: clientX - position.x, y: clientY - position.y })
  }

  const handlePointerMove = useCallback((clientX: number, clientY: number) => {
    if (!dragStart) {
      return
    }
    const nextX = clientX - dragStart.x
    const nextY = clientY - dragStart.y
    setPosition(clampPosition(nextX, nextY, scale))
  }, [clampPosition, dragStart, scale])

  function handleScaleChange(value: number) {
    if (!imageElement) {
      return
    }
    const nextScale = Number(value)
    const centerX = (frameSize.width / 2 - position.x) / scale
    const centerY = (frameSize.height / 2 - position.y) / scale
    const nextPosition = clampPosition(
      frameSize.width / 2 - centerX * nextScale,
      frameSize.height / 2 - centerY * nextScale,
      nextScale
    )
    setScale(nextScale)
    setPosition(nextPosition)
  }

  function handleApply() {
    if (!imageElement || !file) {
      return
    }

    const canvas = document.createElement('canvas')
    canvas.width = resolution.width
    canvas.height = resolution.height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return
    }

    const sourceX = -position.x / scale
    const sourceY = -position.y / scale
    const sourceWidth = frameSize.width / scale
    const sourceHeight = frameSize.height / scale
    const imageType = file.type.startsWith('image/') ? file.type : 'image/jpeg'

    ctx.drawImage(
      imageElement,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      resolution.width,
      resolution.height
    )

    canvas.toBlob(blob => {
      if (!blob) {
        return
      }
      const croppedFile = new File([blob], getFileName(file.name, imageType), { type: imageType })
      onApply(croppedFile)
    }, imageType, 0.92)
  }

  function onModalClose() {
    setDragStart(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={onModalClose} size="md" className="image-crop-modal">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="crop-meta">
          Resolution: {resolution.width} x {resolution.height}
        </div>
        <div
          className="crop-frame"
          style={{ width: frameSize.width, height: frameSize.height }}
          onMouseDown={event => handlePointerDown(event.clientX, event.clientY)}
          onMouseMove={event => handlePointerMove(event.clientX, event.clientY)}
          onMouseUp={() => setDragStart(null)}
          onMouseLeave={() => setDragStart(null)}
          onTouchStart={event => {
            const touch = event.touches[0]
            if (touch) {
              handlePointerDown(touch.clientX, touch.clientY)
            }
          }}
          onTouchMove={event => {
            const touch = event.touches[0]
            if (touch) {
              handlePointerMove(touch.clientX, touch.clientY)
            }
          }}
          onTouchEnd={() => setDragStart(null)}
        >
          {imageSrc && !isLoading && (
            <img
              src={imageSrc}
              alt="Crop source"
              className="crop-image"
              style={{
                width: displayedImageWidth,
                height: displayedImageHeight,
                left: position.x,
                top: position.y
              }}
            />
          )}
        </div>
        <div className="crop-controls">
          <label htmlFor="image-crop-zoom">Zoom</label>
          <input
            id="image-crop-zoom"
            type="range"
            min={minScale}
            max={Math.max(minScale * 3, minScale + 0.1)}
            step={0.01}
            value={scale}
            onChange={event => handleScaleChange(Number(event.target.value))}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button appearance="primary" onClick={handleApply} disabled={!imageElement}>
          Apply
        </Button>
        <Button appearance="subtle" onClick={onModalClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
